import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search, X, User, Phone, Mail, Car, Calendar, ChevronRight,
  StickyNote, Building2, Eye, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iter 160 — Buscador agregado de clientes (Vista 360)
export default function CustomerSearchPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const axiosAuth = useMemo(
    () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  const debounceRef = useRef(null);

  // Carga inicial: últimos clientes con actividad reciente
  useEffect(() => {
    fetchResults("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce sobre la búsqueda
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchResults(q.trim());
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const fetchResults = async (query) => {
    setLoading(true); setError(""); setSearched(query.length > 0);
    try {
      const r = await axiosAuth.get("/customers/search", {
        params: { q: query, limit: 30 },
      });
      setItems(r.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error en la búsqueda");
      setItems([]);
    } finally { setLoading(false); }
  };

  const clear = () => setQ("");

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="customer-search-page">
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-900">Clientes</h1>
        <p className="text-sm text-slate-500 mt-1">
          Busca clientes por nombre, teléfono, email, matrícula o vehículo.
        </p>
      </div>

      {/* Buscador */}
      <Card className="p-3 mb-5">
        <div className="relative flex items-center gap-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ej: 'Jose', '667440816', '1111AAA', 'Fiat 500'..."
            className="pl-10 pr-10 h-10 text-sm"
            data-testid="customer-search-input"
            autoFocus
          />
          {q && (
            <button type="button" onClick={clear}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
              data-testid="customer-search-clear">
              <X className="w-3.5 h-3.5 text-slate-400" />
            </button>
          )}
        </div>
        {loading && (
          <p className="text-xs text-slate-400 mt-2 flex items-center gap-1" data-testid="customer-search-loading">
            <Loader2 className="w-3 h-3 animate-spin" /> Buscando…
          </p>
        )}
      </Card>

      {/* Resultados */}
      {error && (
        <p className="text-sm text-red-600 px-2 py-3">{error}</p>
      )}

      {!loading && !error && items.length === 0 && (
        <Card className="p-10 text-center" data-testid="customer-search-empty">
          {searched ? (
            <>
              <p className="text-sm text-slate-500 mb-2">No se encontraron clientes</p>
              <p className="text-xs text-slate-400">Prueba con otro término de búsqueda</p>
            </>
          ) : (
            <p className="text-sm text-slate-500">
              Busca por nombre, teléfono, email o matrícula
            </p>
          )}
        </Card>
      )}

      {items.length > 0 && (
        <div className="space-y-2" data-testid="customer-search-results">
          {!searched && (
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 px-2 mt-1 mb-2">
              Clientes con actividad reciente
            </p>
          )}
          {items.map((c) => (
            <Card key={c.customer_key} className="p-4 hover:shadow-md transition-shadow"
              data-testid={`customer-result-${c.customer_key}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-slate-900 text-white flex items-center justify-center text-lg font-bold flex-shrink-0">
                  {(c.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-slate-900 truncate">
                      {c.name || "Cliente sin nombre"}
                    </h3>
                    {c.has_internal_notes && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 border border-amber-200" title="Tiene notas internas">
                        <StickyNote className="w-2.5 h-2.5" /> Notas
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-slate-600">
                    {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                    {c.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {c.email}</span>}
                    {c.centers && c.centers.length > 0 && (
                      <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {c.centers.join(" · ")}</span>
                    )}
                  </div>
                  {c.vehicles && c.vehicles.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {c.vehicles.slice(0, 4).map((v) => (
                        <span key={v.plate}
                          className="inline-flex items-center gap-1 text-[11px] bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">
                          <Car className="w-3 h-3 text-slate-400" />
                          <span className="font-mono">{v.plate}</span>
                          {(v.brand || v.model) && (
                            <span className="text-slate-500">· {[v.brand, v.model].filter(Boolean).join(" ")}</span>
                          )}
                        </span>
                      ))}
                      {c.vehicles.length > 4 && (
                        <span className="text-[11px] text-slate-400 px-1.5 py-0.5">+{c.vehicles.length - 4}</span>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 mt-2 text-[11px]">
                    <span className="text-slate-500">
                      <b>{c.appointments_count}</b> citas
                    </span>
                    {c.last_appointment_at && (
                      <span className="text-slate-500">
                        Última: <b>{c.last_appointment_at}</b>
                      </span>
                    )}
                    {c.next_appointment_at && (
                      <span className="text-emerald-700 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Próxima: <b>{c.next_appointment_at}</b>
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/admin/clientes/${encodeURIComponent(c.customer_key)}`)}
                  data-testid={`customer-view-${c.customer_key}`}
                  className="flex-shrink-0"
                >
                  <Eye className="w-3.5 h-3.5 mr-1" /> Ver ficha <ChevronRight className="w-3 h-3 ml-0.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
